import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Modal,
  Switch,
  Tooltip,
  IconButton,
  Typography
} from '@mui/material';

import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';

import {
  GetCompanyVectorPrefsByCompanyBIZcoreTD,
  DeleteCompanyVectorPrefBIZcoreTD,
  UpdateCompanyVectorPrefBIZcoreTD
} from '../../../../../redux/actions/business/core/tradeDefencesActions';

import { CLEAR_COMPANY_VECTOR_PREFS_DETAIL_BIZ_CORE_TD } from '../../../../../redux/actionTypes';

import global from "../../../../../global.scss";

const CompanyVectorPrefsDetailModal = ({
  open,
  setOpen,
  companyId,
  handleGetMain
}) => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.tradeDefence"]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [prefsList, setPrefsList] = useState([]);

  useEffect(() => {
    try {
      if (open && companyId) {
        dispatch({ type: CLEAR_COMPANY_VECTOR_PREFS_DETAIL_BIZ_CORE_TD });
        handleGetPrefs();
      }
    } catch (err) {
      console.log(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId]);

  useEffect(() => {
    try {
      const records = [...(state.companyVectorPrefsDetailList?.records || [])];
      records.sort((a, b) => {
        const pillarA = a.ref_vector_id?.pillar_number ?? 999;
        const pillarB = b.ref_vector_id?.pillar_number ?? 999;
        if (pillarA !== pillarB) return pillarA - pillarB;
        return (a.ref_vector_id?.order ?? 999) - (b.ref_vector_id?.order ?? 999);
      });
      setPrefsList(records);
    } catch (err) {}
  }, [state]);

  async function handleGetPrefs() {
    await dispatch(GetCompanyVectorPrefsByCompanyBIZcoreTD(companyId, true, setLoading));
  }

  const handleClose = () => {
    setOpen(false);
    dispatch({ type: CLEAR_COMPANY_VECTOR_PREFS_DETAIL_BIZ_CORE_TD });
  };

  const handleDelete = (prefId) => {
    const loadingSetter = (val) => setDeletingId(val === true ? prefId : null);
    dispatch(DeleteCompanyVectorPrefBIZcoreTD(prefId, loadingSetter)).then(() => {
      handleGetPrefs().then(() => {
        handleGetMain(() => {});
      });
    });
  };

  const handleToggleActive = (pref) => {
    const loadingSetter = (val) => setTogglingId(val === true ? pref.id : null);
    dispatch(UpdateCompanyVectorPrefBIZcoreTD(pref.id, { is_active: !pref.is_active }, loadingSetter)).then(() => {
      handleGetPrefs().then(() => {
        handleGetMain(() => {});
      });
    });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box className="flexCenterCenterRow" width={"100vw"} height={"100vh"}>
        <Box maxWidth={"sm"} className="modal" sx={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
          <Box width={"100%"} sx={{ padding: "15px", display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            <Typography variant='h6'
              sx={{ color: global["primary"] }} mb={"5px"}>
              Compliance Vectors
            </Typography>
            <Typography variant='caption' color={"grey"}
              sx={{ display: "block" }} mb={"20px"}>
              Company: {companyId || "—"}
            </Typography>

            {
              loading
              ?
              <Box height={"120px"} className="flexCenterCenterRow" sx={{ flexShrink: 0 }}>
                <CircularProgress size={24} />
              </Box>
              :
                prefsList.length === 0
                ?
                <Box height={"80px"} className="flexCenterCenterRow" sx={{ flexShrink: 0 }}>
                  <Typography variant="body2" color={"grey"}>
                    No vectors assigned to this company
                  </Typography>
                </Box>
                :
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
                  {
                    prefsList.map((pref) => (
                      <Box
                        key={pref.id}
                        className="flexCenterSBRow"
                        sx={{
                          padding: "12px 14px",
                          borderRadius: "10px",
                          background: "rgba(44, 45, 47, 0.6)",
                          boxShadow: "0 2px 12px 0 rgba(0,0,0,0.35), 0 1px 3px 0 rgba(0,0,0,0.25)",
                          border: "1px solid rgba(80, 80, 82, 0.45)",
                        }}
                      >
                        <Box>
                          <Box className="flexCenter_Row" gap={"8px"} mb={"2px"}>
                            <Chip
                              size="small"
                              label={`#${pref.ref_vector_id?.order ?? "—"}`}
                              sx={{ fontSize: "10px", height: "18px" }}
                            />
                            <Typography variant="body2">
                              {pref.ref_vector_id?.name || "—"}
                            </Typography>
                          </Box>
                          <Box className="flexCenter_Row" gap={"6px"} mt={"3px"}>
                            <Typography variant="caption" color={"grey"}>
                              Pillar {pref.ref_vector_id?.pillar_number ?? "—"}
                            </Typography>
                            <Chip
                              size="small"
                              label={pref.ref_vector_id?.is_active ? "Global: Active" : "Global: Disabled"}
                              color={pref.ref_vector_id?.is_active ? "success" : "error"}
                              sx={{ fontSize: "9px", height: "16px" }}
                            />
                          </Box>
                        </Box>
                        <Box className="flexCenter_Row" gap={"4px"}>
                          <Tooltip title={pref.is_active ? "Deactivate" : "Activate"}>
                            <span>
                              {togglingId === pref.id
                                ? <CircularProgress size={20} sx={{ mx: "9px" }} />
                                : <Switch
                                    checked={pref.is_active}
                                    disabled={deletingId === pref.id}
                                    onChange={() => handleToggleActive(pref)}
                                  />
                              }
                            </span>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <span>
                              <IconButton
                                size="small"
                                disabled={deletingId === pref.id || togglingId === pref.id}
                                onClick={() => handleDelete(pref.id)}
                              >
                                {
                                  deletingId === pref.id
                                  ? <CircularProgress size={16} />
                                  : <DeleteOutlinedIcon fontSize="small" sx={{ color: "#b24020" }} />
                                }
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                    ))
                  }
                </Box>
            }
          </Box>
          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />
          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px" }}>
            <Button variant='text' onClick={handleClose}>
              Close
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default CompanyVectorPrefsDetailModal;
